import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * TouchDateInput — gloved-finger-friendly date entry.
 *
 * Problem with <input type="date">:
 *   - Different native pickers on every OS/browser (wildly inconsistent).
 *   - iOS requires scrolling a tiny wheel; Android pops a calendar that
 *     defaults to the current year (bad for DOBs 40+ years back).
 *   - Both require precise taps and fail with gloves.
 *
 * This replacement gives three big MM / DD / YYYY number fields with
 * inputmode="numeric" so the on-screen keyboard is a tactile number pad.
 * Auto-advances between fields, auto-zero-pads on blur, validates on
 * commit, and emits a clean YYYY-MM-DD string (or null).
 *
 * Includes quick-fill chips for common "age" shortcuts so providers can
 * enter DOB-from-age when the patient can't provide an exact date.
 */

interface Props {
  value: string | null; // ISO YYYY-MM-DD
  onChange: (v: string | null) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
  warn?: boolean; // visual warning state (e.g. placeholder DOB detected)
  /** If true, show age-based quick-fill chips. Default true for patient DOB. */
  showAgeShortcuts?: boolean;
  /** Max year (default = current year). */
  maxYear?: number;
  /** Min year (default = current year - 120). */
  minYear?: number;
  id?: string;
  className?: string;
}

export function TouchDateInput({
  value, onChange, label, hint, disabled, warn, showAgeShortcuts = true,
  maxYear, minYear, id, className,
}: Props) {
  const parsed = useMemo(() => parseISO(value), [value]);
  const [month, setMonth] = useState(parsed?.month ?? '');
  const [day, setDay] = useState(parsed?.day ?? '');
  const [year, setYear] = useState(parsed?.year ?? '');
  const [err, setErr] = useState<string | null>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const thisYear = new Date().getFullYear();
  const yMax = maxYear ?? thisYear;
  const yMin = minYear ?? thisYear - 120;

  // Keep local state in sync when `value` changes externally (e.g. from ID scan)
  useEffect(() => {
    const p = parseISO(value);
    setMonth(p?.month ?? '');
    setDay(p?.day ?? '');
    setYear(p?.year ?? '');
    setErr(null);
  }, [value]);

  function commit(m: string, d: string, y: string) {
    if (!m && !d && !y) { onChange(null); setErr(null); return; }
    if (!m || !d || !y) { setErr(null); return; } // partial, wait

    const mi = parseInt(m, 10);
    const di = parseInt(d, 10);
    const yi = parseInt(y, 10);

    if (Number.isNaN(mi) || mi < 1 || mi > 12) { setErr('Month must be 01–12'); return; }
    if (Number.isNaN(di) || di < 1 || di > 31) { setErr('Day must be 01–31'); return; }
    if (Number.isNaN(yi) || yi < yMin || yi > yMax) { setErr(`Year must be ${yMin}–${yMax}`); return; }

    // Use UTC to avoid DST / timezone-offset shifts of the ISO date.
    const dt = new Date(Date.UTC(yi, mi - 1, di));
    if (dt.getUTCFullYear() !== yi || dt.getUTCMonth() !== mi - 1 || dt.getUTCDate() !== di) {
      setErr('Not a real calendar date');
      return;
    }

    setErr(null);
    onChange(iso(yi, mi, di));
  }

  // Auto-advance when a field is full
  function onMonthInput(v: string) {
    const clean = v.replace(/\D/g, '').slice(0, 2);
    setMonth(clean);
    if (clean.length === 2) dayRef.current?.focus();
  }
  function onDayInput(v: string) {
    const clean = v.replace(/\D/g, '').slice(0, 2);
    setDay(clean);
    if (clean.length === 2) yearRef.current?.focus();
  }
  function onYearInput(v: string) {
    const clean = v.replace(/\D/g, '').slice(0, 4);
    setYear(clean);
  }

  function onMonthBlur() {
    const m = month.length === 1 ? `0${month}` : month;
    setMonth(m);
    commit(m, day.length === 1 ? `0${day}` : day, year);
  }
  function onDayBlur() {
    const d = day.length === 1 ? `0${day}` : day;
    setDay(d);
    commit(month.length === 1 ? `0${month}` : month, d, year);
  }
  function onYearBlur() {
    commit(
      month.length === 1 ? `0${month}` : month,
      day.length === 1 ? `0${day}` : day,
      year,
    );
  }

  function applyAge(yrs: number) {
    // Estimate DOB from age — July 1 of (thisYear - yrs). Field documenter
    // can tighten the month/day if they learn more.
    const y = thisYear - yrs;
    const m = '07';
    const d = '01';
    setMonth(m); setDay(d); setYear(String(y));
    commit(m, d, String(y));
  }

  function clear() {
    setMonth(''); setDay(''); setYear(''); setErr(null);
    onChange(null);
    monthRef.current?.focus();
  }

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label htmlFor={id ? `${id}-month` : undefined} className="data-label mb-1 block">
          {label}
        </label>
      )}
      <div
        className={cn(
          'input-field flex items-center gap-2 px-2 py-1.5',
          warn && 'border-ems-amber',
          err && 'border-red-600',
          disabled && 'opacity-60 pointer-events-none',
        )}
      >
        <Calendar size={16} className="text-slate-500 shrink-0" />
        <div className="flex flex-1 items-center gap-1 font-mono text-lg tracking-wider">
          <input
            ref={monthRef}
            id={id ? `${id}-month` : undefined}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={month}
            onChange={(e) => onMonthInput(e.target.value)}
            onBlur={onMonthBlur}
            onFocus={(e) => e.currentTarget.select()}
            placeholder="MM"
            maxLength={2}
            disabled={disabled}
            className="w-10 bg-transparent text-center outline-none placeholder:text-slate-600"
            aria-label="Month"
          />
          <span className="text-slate-600">/</span>
          <input
            ref={dayRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={day}
            onChange={(e) => onDayInput(e.target.value)}
            onBlur={onDayBlur}
            onFocus={(e) => e.currentTarget.select()}
            placeholder="DD"
            maxLength={2}
            disabled={disabled}
            className="w-10 bg-transparent text-center outline-none placeholder:text-slate-600"
            aria-label="Day"
          />
          <span className="text-slate-600">/</span>
          <input
            ref={yearRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={year}
            onChange={(e) => onYearInput(e.target.value)}
            onBlur={onYearBlur}
            onFocus={(e) => e.currentTarget.select()}
            placeholder="YYYY"
            maxLength={4}
            disabled={disabled}
            className="w-16 bg-transparent text-center outline-none placeholder:text-slate-600"
            aria-label="Year"
          />
        </div>
        {(month || day || year) && !disabled && (
          <button type="button" onClick={clear} className="text-slate-500 hover:text-slate-200" aria-label="Clear date">
            <X size={14} />
          </button>
        )}
      </div>

      {err && <p className="mt-1 text-xs text-red-400">{err}</p>}

      {showAgeShortcuts && !disabled && (
        /* One row, always — chips share the available width via
           1fr columns so they line up cleanly on every viewport.
           No prefix label, no hint text below; the affordance is
           self-explanatory once the user taps a number. */
        <div
          className="mt-1.5 grid gap-1"
          style={{ gridTemplateColumns: 'repeat(10, minmax(0, 1fr))' }}
        >
          {[1, 5, 10, 18, 25, 40, 55, 65, 75, 85].map((age) => (
            <button
              key={age}
              type="button"
              onClick={() => applyAge(age)}
              className="rounded-sm border border-slate-700 bg-slate-800/60 px-1 py-0.5 text-xs text-slate-200 hover:bg-slate-700 hover:text-slate-100 min-h-[28px]"
              title={`DOB ≈ ${thisYear - age}-07-01`}
            >
              {age}
            </button>
          ))}
        </div>
      )}

      {/* Hint text removed at user request — directions clutter
          the chip row. err still surfaces above. */}
    </div>
  );
}

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------
function parseISO(s: string | null): { month: string; day: string; year: string } | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return { year: m[1], month: m[2], day: m[3] };
}

function iso(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
